import { FC, memo, useMemo } from 'react'
import { useSnapshot } from 'valtio'
import { GlobalStyles } from '@mui/material'
import ContentRenderer from '@app/components/common/ContentRenderer'
import { QuestionFeedback, QuestionGroupFeedback, GeneralFeedback } from '@app/components/feedback'
import VideoFeedback from '@app/components/feedback/VideoFeedback'
import { InfoControlLayout } from '@app/components/ui/layouts/InfoControlLayout'
import { InteractionLayout } from '@app/components/ui/layouts/InteractionLayout'
import { isHtmlMarkupEmpty, useAssessmentItem, useAssessmentItemStyles } from '@app/helpers'
import { CurrentTestState, CurrentUserAssessmentState, ScoringState } from '@app/storage'
import { AssessmentStatus } from '@app/firebase/api'
import WritingScorePanel from '../WritingScorePanel/WritingScorePanel'
import { AssessmentItemWrapper } from './AssessmentItemWrapper'

interface AssessmentItemProps {}

const AssessmentItemComponent: FC<AssessmentItemProps> = memo(() => {
  const { currentItemResponse, currentTestPart } = useSnapshot(CurrentTestState)
  const { status } = useSnapshot(CurrentUserAssessmentState)
  const { groupMode, isPreviewMode } = useSnapshot(ScoringState)

  const {
    currentItem,
    interaction,
    infoControl,
    hasInlineInteractions,
    generalFeedback,
    videoThumbnail,
    videoIframe,
  } = useAssessmentItem()

  // Convert generalFeedback HTML to React elements using ContentRenderer
  const generalFeedbackElement = useMemo(() => {
    if (!generalFeedback || isHtmlMarkupEmpty(generalFeedback)) return null

    return (
      <ContentRenderer
        content={generalFeedback}
        itemId={currentItem?.id}
        resourceId={currentItem?.resourceId}
      />
    )
  }, [generalFeedback, currentItem?.id, currentItem?.resourceId])

  const styles = useAssessmentItemStyles(currentItem)

  const isCompleted = status === AssessmentStatus.completed

  return (
    <AssessmentItemWrapper>
      {styles && <GlobalStyles styles={styles} />}

      {currentItem && (
        <>
          {infoControl.trim() && (
            <InfoControlLayout>
              <ContentRenderer
                itemId={currentItem?.id}
                content={infoControl}
                resourceId={currentItem?.resourceId}
              />
            </InfoControlLayout>
          )}
          {currentItemResponse?.id && interaction && (
            <InteractionLayout
              itemId={currentItemResponse?.itemId}
              twoColumn={currentTestPart?.twoColumnLayout}
              reviewMode={isCompleted}
            >
              <ContentRenderer
                itemId={currentItem?.id}
                content={interaction}
                resourceId={currentItem?.resourceId}
                generalFeedback={generalFeedbackElement}
              />
              {hasInlineInteractions && (
                <>
                  {groupMode && !isPreviewMode ? (
                    <QuestionGroupFeedback itemId={currentItem?.id} />
                  ) : (
                    <QuestionFeedback itemId={currentItem?.id} generalFeedback={generalFeedbackElement} />
                  )}
                </>
              )}
              {groupMode && !isPreviewMode && <GeneralFeedback generalFeedback={generalFeedbackElement} />}

              {isCompleted && <VideoFeedback thumbnailHtml={videoThumbnail} iframeHtml={videoIframe} />}
              {isCompleted && <WritingScorePanel />}
            </InteractionLayout>
          )}
        </>
      )}
    </AssessmentItemWrapper>
  )
})

export default AssessmentItemComponent
